import { PromiseExtended } from 'dexie';
import { isDocumentSettled } from './documentSettlement';
import db from './index';
import {
  getErrorMsg,
  isDexieConstraintError,
  logWithRethrow,
  Operation,
} from './helpers';
import { wrapQuery } from './queryWrapper';
import {
  DocumentEntity,
  DocumentInsertEntity,
} from 'serviceWorker/repository/document/entity';
import { DocumentDrawing } from 'shared/dtos/in/document';
import { DocumentParentRelation } from 'serviceWorker/repository/document/relations';
import {
  DocumentBindings,
  DocumentParentQuery,
} from 'shared/domain/document/documentModel';
import { DrawingChanges } from 'shared/domain/document/drawingChanges';
import { nowISO } from 'shared/utils/date/dates';
import { UploadStatus, isUploadStatusSuccess } from 'shared/types/uploadStatus';
import type { ImageConvertStatus } from 'shared/domain/imageConvertStatus/imageConvertStatus';
import {
  isSyncStatusSuccess,
  SyncStatus,
} from 'shared/domain/entitySyncStatus/syncStatus';
import {
  makeDefaultGetManyByIds,
  makeDefaultGetOne,
  makeDefaultRemoveBatch,
} from './defaultDaoFactories';
import { LogLevel } from 'shared/types/logger';
import { isDefinedValue } from 'shared/utils/value';
import { assignRemoteIdFactory } from './assignRemoteIdFactory';

function getLatestDrawing(
  drawings: DocumentDrawing[] | undefined
): DocumentDrawing | undefined {
  if (!drawings || drawings.length === 0) {
    return undefined;
  }

  return drawings[drawings.length - 1];
}

function updateLatestDrawing(
  drawings: DocumentDrawing[] | undefined,
  patch: Partial<DocumentDrawing>
): DocumentDrawing[] {
  const current = drawings ?? [];

  if (current.length === 0) {
    return [patch as DocumentDrawing];
  }

  const latestIndex = current.length - 1;
  return current.map((drawing, index) =>
    index === latestIndex ? ({ ...drawing, ...patch } as DocumentDrawing) : drawing
  );
}

function keepLocalDrawingDataOnUnuploadedStatus(
  documentToInsert: DocumentInsertEntity,
  document: DocumentEntity
): DocumentInsertEntity {
  if (
    documentToInsert._id &&
    isUploadStatusSuccess(documentToInsert.data?.uploadStatus) &&
    isSyncStatusSuccess(document.syncStatus) &&
    document.localData
  ) {
    documentToInsert.localData = undefined;
  }

  if (!isSyncStatusSuccess(document.syncStatus)) {
    documentToInsert.deleted = document.deleted;
  }

  if (
    (!isSyncStatusSuccess(document.syncStatus) ||
      !isSyncStatusSuccess(document.drawingSyncStatus)) &&
    documentToInsert.data
  ) {
    documentToInsert.syncStatus = document.syncStatus;
    documentToInsert.data.isDrawn = Boolean(document.data?.isDrawn);
  }

  if (documentToInsert.data?.isDrawn) {
    const latestDrawing = getLatestDrawing(documentToInsert.drawings);
    if (
      !isUploadStatusSuccess(latestDrawing?.mergedUploadStatus) ||
      !isSyncStatusSuccess(document.drawingSyncStatus)
    ) {
      documentToInsert.drawingSyncStatus = document.drawingSyncStatus;
      documentToInsert.data.mergedSrc = document.data?.mergedSrc;
      documentToInsert.data.thumbnailMergedSrc =
        document.data?.thumbnailMergedSrc;
    }
    if (
      !isUploadStatusSuccess(latestDrawing?.drawnUploadStatus) ||
      !isSyncStatusSuccess(document.drawingSyncStatus)
    ) {
      documentToInsert.drawingSyncStatus = document.drawingSyncStatus;
      documentToInsert.data.drawingSrc = document.data?.drawingSrc;
    }
  }

  return documentToInsert;
}

const update = wrapQuery(
  db,
  (
    documentFindStrategy: CallableFunction,
    data: DocumentInsertEntity
  ): PromiseExtended<any> => {
    return documentFindStrategy().then((document: DocumentEntity) => {
      if (!document) {
        throw new Error('Cannot find document to update');
      } else {
        const documentWithLocalDrawingData =
          keepLocalDrawingDataOnUnuploadedStatus(data, document);

        return db.documents.update(
          document.localId,
          documentWithLocalDrawingData
        );
      }
    });
  }
);


/** The fields of a stored document a change may set. */
export interface DocumentChanges {
  description?: string | undefined;
  _id?: string | undefined;
  deleted?: boolean;
  localData?: undefined;
  drawingUploadStatus?: UploadStatus | undefined;
  syncStatus?: SyncStatus | undefined;
  isExportAllowed?: boolean;
  imageConvertStatus?: ImageConvertStatus;
  data?: { uploadStatus?: UploadStatus };
}
export const getOneByRemote = makeDefaultGetOne<string, DocumentEntity>(
  db,
  db.documents,
  '_id'
);

export const getOne = makeDefaultGetOne<number, DocumentEntity>(
  db,
  db.documents,
  'localId'
);

export const upsertBatch = wrapQuery(
  db,
  async (data: DocumentInsertEntity[]): Promise<unknown> => {
    if (data.length === 0) return;

    // Filter out documents without _id and deduplicate (keep last occurrence)
    const entityMap = new Map<string, DocumentInsertEntity>();
    for (const doc of data) {
      if (doc._id) {
        entityMap.set(doc._id, doc);
      }
    }
    const uniqueEntities = Array.from(entityMap.values());
    const incomingIds = Array.from(entityMap.keys());

    if (incomingIds.length === 0) return;

    // Fetch only the existing records we need (not ALL keys!)
    const existingRecords = await db.documents
      .where('_id')
      .anyOf(incomingIds)
      .toArray();

    // Build map of _id -> existing record for localId and local data preservation
    const existingMap = new Map<string, DocumentEntity>();
    for (const record of existingRecords) {
      if (record._id) {
        existingMap.set(record._id, record);
      }
    }

    // Prepare entities with localId for existing ones (enables bulkPut upsert)
    // Also preserve local drawing data for unuploaded documents
    const entitiesToUpsert = uniqueEntities.map((entity) => {
      const existing = existingMap.get(entity._id!);
      if (existing) {
        // Existing record - include localId for proper upsert
        // Also preserve local data for unuploaded status
        const preserved = keepLocalDrawingDataOnUnuploadedStatus(entity, existing);
        return { ...preserved, localId: existing.localId };
      }
      // New record - no localId, will get auto-incremented
      return entity;
    });

    // Single bulkPut handles both inserts and updates efficiently
    return db.documents
      // @ts-ignore localId may or may not be present
      .bulkPut(entitiesToUpsert)
      .catch((e) => {
        logWithRethrow({
          logLevel: LogLevel.INFO,
          msg: getErrorMsg(Operation.upsertBatch, 'documents'),
          errorObj: e,
          additionalInfo: { query: { data } },
        });
      });
  }
);

export const addDocument = wrapQuery(
  db,
  async (document: DocumentInsertEntity): Promise<number> => {
    return db.documents.add(document as DocumentEntity).catch((e: unknown) => {
      if (isDexieConstraintError(e)) {
        const documentFindStrategy = (): Promise<
          DocumentEntity | undefined
        > => {
          if (!document._id) {
            return Promise.resolve(undefined);
          }
          return getOneByRemote(document._id);
        };
        return update(documentFindStrategy, document);
      }
      logWithRethrow({
        logLevel: LogLevel.INFO,
        msg: getErrorMsg(Operation.add, 'documents'),
        errorObj: e,
        additionalInfo: { query: { data: document } },
      });
    });
  }
);

export const getByIds = makeDefaultGetManyByIds<number, DocumentEntity>(
  db,
  db.documents,
  'localId'
);

export const getByQuery = wrapQuery(
  db,
  async (query: DocumentParentQuery): Promise<DocumentEntity[]> => {
    const entries = Object.entries(query).filter(
      (entry): entry is [string, string] => typeof entry[1] === 'string'
    );
    if (entries.length === 0) {
      return [];
    }
    
    // If query has multiple keys (e.g., {inspectionId, templateId}), fetch for each and merge
    if (entries.length > 1) {
      try {
        const results = await Promise.all(
          entries.map(([key, value]) =>
            db.documents
              .where(key)
              .equals(value)
              .toArray()
          )
        );
        // Flatten and deduplicate by localId
        const allDocs = results.flat();
        const uniqueDocs = Array.from(
          new Map(allDocs.map(doc => [doc.localId, doc])).values()
        );
        return uniqueDocs;
      } catch (e) {
        return logWithRethrow({
          logLevel: LogLevel.INFO,
          msg: getErrorMsg(Operation.getByQuery, 'documents'),
          errorObj: e,
          additionalInfo: {
            query,
          },
        });
      }
    }
    
    // Single key query (legacy behavior)
    const [key, value] = entries[0];

    return db.documents
      .where(key)
      .equals(value)
      .toArray()
      .catch((e) =>
        logWithRethrow({
          logLevel: LogLevel.INFO,
          msg: getErrorMsg(Operation.getByQuery, 'documents'),
          errorObj: e,
          additionalInfo: {
            query,
          },
        })
      );
  }
);

export const removeBatch = makeDefaultRemoveBatch<number, DocumentEntity>(
  db,
  db.documents,
  'localId'
);

export const remove = wrapQuery(
  db,
  async (id: number): Promise<unknown> => {
    return db.documents.delete(id).catch((e) =>
      logWithRethrow({
        logLevel: LogLevel.INFO,
        msg: getErrorMsg(Operation.remove, 'documents'),
        errorObj: e,
        additionalInfo: { query: { id } },
      })
    );
  }
);

/** The issues' own documents, without those of their events and incoming emails (bound to the issue too). */
export const getOwnDocumentsOfIssues = wrapQuery(
  db,
  async (issueIds: string[]): Promise<DocumentEntity[]> =>
    db.documents
      .where('issueId')
      .anyOf(issueIds)
      .filter((document) => !document.eventId && !document.incomingEmailMessageId)
      .toArray()
      .catch((e) =>
        logWithRethrow({
          logLevel: LogLevel.INFO,
          msg: getErrorMsg(Operation.getByQuery, 'documents'),
          errorObj: e,
          additionalInfo: { query: { issueCount: issueIds.length } },
        })
      )
);

export const removeBatchByParentId = wrapQuery(
  db,
  async (query: DocumentParentRelation): Promise<any> => {
    const [key, value] = Object.entries(query)[0];

    await db.documents
      .where(key)
      .anyOf(value)
      .delete()
      .catch((e) =>
        logWithRethrow({
          logLevel: LogLevel.INFO,
          msg: getErrorMsg(Operation.removeBatch, 'documents'),
          errorObj: e,
          additionalInfo: { query },
        })
      );
    return query;
  }
);

/**
 * Removes the settled documents of every parent of a kind (`issueId`,
 * `inspectionId`, `templateId`), as the clear of the parents' table does.
 * A document that waits to be sent stays: its local data is the only copy
 * (a photo taken offline) and its queued request needs it.
 */
export const removeSettledBatchByParentType = wrapQuery(
  db,
  async (key: keyof DocumentBindings): Promise<any> => {
    await db.documents
      .where(key)
      .notEqual('')
      .and(isDocumentSettled)
      .delete()
      .catch((e) =>
        logWithRethrow({
          logLevel: LogLevel.INFO,
          msg: getErrorMsg(Operation.removeBatch, 'documents'),
          errorObj: e,
          additionalInfo: { query: { key } },
        })
      );
    return key;
  }
);

/**
 * Removes every settled document, as a project switch clears the project's
 * data. A document that waits to be sent stays, whatever project it belongs
 * to: its queued request names that project and needs the local file (a photo
 * taken offline is the only copy), and it is settled, then removed, once sent.
 */
export const removeSettled = wrapQuery(db, async (): Promise<void> => {
  await db.documents
    .filter(isDocumentSettled)
    .delete()
    .catch((e) =>
      logWithRethrow({
        logLevel: LogLevel.INFO,
        msg: getErrorMsg(Operation.removeBatch, 'documents'),
        errorObj: e,
        additionalInfo: null,
      })
    );
});

export const updateOne = wrapQuery(
  db,
  async (
    localId: number,
    changes: DocumentChanges,
    options?: { skipModifyTime: boolean }
  ): Promise<any> => {
    const modifiedAt = options?.skipModifyTime
      ? {}
      : { modifiedAt: nowISO() };
    const {
      'data.drawnUploadStatus': drawnUploadStatus,
      data: changesData,
      ...restChanges
    } = changes as DocumentChanges & {
      'data.drawnUploadStatus'?: UploadStatus;
      data?: Partial<DocumentEntity['data']> | null;
    };
    const updatePayload: Partial<DocumentEntity> & { modifiedAt?: string } = {
      ...restChanges,
      ...modifiedAt,
    };
    if (isDefinedValue(changesData)) {
      const doc = await db.documents.get(localId);
      const existingData = doc?.data ?? null;
      updatePayload.data = {
        ...(existingData && typeof existingData === 'object' ? existingData : {}),
        ...changesData,
      } as DocumentEntity['data'];
    }
    if (drawnUploadStatus !== undefined) {
      const doc = await db.documents.get(localId);
      (updatePayload as Partial<DocumentEntity>).drawings = updateLatestDrawing(
        doc?.drawings,
        { drawnUploadStatus }
      );
    }
    return db.documents
      .update(localId, updatePayload)
      .catch((e) =>
        logWithRethrow({
          logLevel: LogLevel.INFO,
          msg: getErrorMsg(Operation.updateOne, 'documents'),
          errorObj: e,
          additionalInfo: { query: changes },
        })
      );
  }
);

type DrawingUpdateChanges = Partial<DrawingChanges> & {
  drawings?: DocumentDrawing[];
  'data.drawnUploadStatus'?: UploadStatus;
};

export const updateDrawing = wrapQuery(
  db,
  async (localId: number, changes: DrawingUpdateChanges | any): Promise<any> => {
    const doc = await db.documents.get(localId);
    if (!doc) {
      throw new Error('Cannot find document to update');
    }
    const modifiedAt = { modifiedAt: (changes?.modifiedAt as string) ?? nowISO() };
    const updatePayload: Partial<DocumentEntity> & { modifiedAt?: string } = {
      ...modifiedAt,
    };

    const flat = changes as DrawingUpdateChanges;
    if (flat.drawingSyncStatus !== undefined) {
      updatePayload.drawingSyncStatus = flat.drawingSyncStatus;
    }
    if (flat.drawings !== undefined) {
      updatePayload.drawings = flat.drawings as DocumentDrawing[];
    }
    const legacyDrawnStatus = (changes as Record<string, unknown>)['data.drawnUploadStatus'];
    if (legacyDrawnStatus !== undefined) {
      (updatePayload as Partial<DocumentEntity>).drawings = updateLatestDrawing(
        doc.drawings,
        { drawnUploadStatus: legacyDrawnStatus as UploadStatus }
      );
    }

    if (
      flat.isDrawn !== undefined ||
      flat.drawingSrc !== undefined ||
      flat.mergedSrc !== undefined ||
      flat.thumbnailMergedSrc !== undefined ||
      flat.fileType !== undefined
    ) {
      const existingData = doc.data ?? {};
      updatePayload.data = {
        ...(existingData && typeof existingData === 'object' ? existingData : {}),
        ...(flat.isDrawn !== undefined && { isDrawn: flat.isDrawn }),
        ...(flat.drawingSrc !== undefined && { drawingSrc: flat.drawingSrc }),
        ...(flat.mergedSrc !== undefined && { mergedSrc: flat.mergedSrc }),
        ...(flat.thumbnailMergedSrc !== undefined && {
          thumbnailMergedSrc: flat.thumbnailMergedSrc,
        }),
        ...(flat.fileType !== undefined && { type: flat.fileType }),
      } as DocumentEntity['data'];
    }

    return db.documents
      .update(localId, updatePayload)
      .catch((e) =>
        logWithRethrow({
          logLevel: LogLevel.INFO,
          msg: getErrorMsg(Operation.updateDrawing, 'documents'),
          errorObj: e,
          additionalInfo: { query: changes },
        })
      );
  }
);

/** The server id of a document created here; drops a copy a sync stored first (PT-7278). */
export const assignRemoteId = assignRemoteIdFactory(db, 'documents', db.documents);
